import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { BillableItemCategory } from '@ee/common/models';

@Injectable({ providedIn: 'root' })
export class BillableItemCategoryService {
  private resourceUrl: string;
  private cachedCategories$: Observable<BillableItemCategory[]> | null = null;

  constructor(private http: HttpClient, @Inject('environment') private environment) {
    this.resourceUrl = this.environment.api_prefix + 'api/billable-items/categories';
  }

  getCategories(): Observable<BillableItemCategory[]> {
    if (!this.cachedCategories$) {
      this.cachedCategories$ = this.http.get<BillableItemCategory[]>(this.resourceUrl).pipe(
        shareReplay(1)
      );
    }
    return this.cachedCategories$;
  }

  /** Clears the cached categories so the next getCategories() call fetches fresh data */
  invalidateCategoriesCache(): void {
    this.cachedCategories$ = null;
  }

  createCategory(name: string, costType?: string | null): Observable<BillableItemCategory> {
    return this.http.post<BillableItemCategory>(this.resourceUrl, {
      name,
      ...(costType !== undefined && { cost_type: costType }),
    }).pipe(tap(() => this.invalidateCategoriesCache()));
  }

  updateCategory(id: string, name: string, costType?: string | null): Observable<BillableItemCategory> {
    return this.http.put<BillableItemCategory>(`${this.resourceUrl}/${id}`, {
      name,
      ...(costType !== undefined && { cost_type: costType }),
    }).pipe(tap(() => this.invalidateCategoriesCache()));
  }

  deleteCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.resourceUrl}/${id}`).pipe(
      tap(() => this.invalidateCategoriesCache())
    );
  }

  batchCreateCategories(names: string[]): Observable<BillableItemCategory[]> {
    return this.http.post<BillableItemCategory[]>(`${this.resourceUrl}/batch`, { names }).pipe(
      tap(() => this.invalidateCategoriesCache())
    );
  }

  /** Persists a new custom-category order (EE-211). `orderedIds` are the org's custom category ids in the desired order. */
  reorderCategories(orderedIds: string[]): Observable<BillableItemCategory[]> {
    return this.http.put<BillableItemCategory[]>(`${this.resourceUrl}/reorder`, { ordered_ids: orderedIds }).pipe(
      tap(() => this.invalidateCategoriesCache())
    );
  }

  /** Sets which categories are hidden from selection dropdowns (EE-216). `hiddenIds` = all currently-hidden category ids. */
  setHiddenCategories(hiddenIds: string[]): Observable<BillableItemCategory[]> {
    return this.http.put<BillableItemCategory[]>(`${this.resourceUrl}/hidden`, { hidden_ids: hiddenIds }).pipe(
      tap(() => this.invalidateCategoriesCache())
    );
  }

  getDescriptionSuggestions(): Observable<string[]> {
    return this.http.get<string[]>(`${this.resourceUrl}/description-suggestions`);
  }
}
